/**
 * The Microsoft → Supabase session bridge, minus React.
 *
 * A Microsoft-SSO user holds an Entra session immediately, but Flow only works
 * once that has been exchanged for a Supabase session (see
 * microsoftTokenExchange). When the exchange can't be completed silently, the
 * bridge sends the user through an invisible `prompt: 'none'` authorize to mint
 * a token whose nonce we know.
 *
 * Both pieces here exist because of the same failure: that redirect used to be
 * guarded by a bare sessionStorage flag. sessionStorage survives
 * window.location.reload(), so after one failed attempt the tab could never try
 * again — and the stalled-access card's own advice ("reloading the page usually
 * fixes it") was inert. Users sat on "Still verifying access…" indefinitely.
 */

import type { ExchangeFailureReason } from '@/lib/microsoftTokenExchange';

export const BRIDGE_REDIRECT_KEY = 'flow_ms_bridge_redirect_at';

/** Long enough that a failing exchange can't redirect-loop, short enough that a
 *  deliberate reload is a real retry. */
export const BRIDGE_REDIRECT_COOLDOWN_MS = 60_000;

/**
 * May the bridge send the user through Entra again? A cooldown rather than a
 * one-shot: loop protection that still expires.
 */
export function shouldAttemptBridgeRedirect(lastAttemptAt: number | null, now: number): boolean {
  if (lastAttemptAt === null || !Number.isFinite(lastAttemptAt)) return true;
  return now - lastAttemptAt >= BRIDGE_REDIRECT_COOLDOWN_MS;
}

/** Read the last redirect timestamp; unreadable storage reads as "never". */
export function readBridgeRedirectAt(): number | null {
  try {
    const raw = sessionStorage.getItem(BRIDGE_REDIRECT_KEY);
    if (!raw) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function markBridgeRedirect(now: number): void {
  try {
    sessionStorage.setItem(BRIDGE_REDIRECT_KEY, String(now));
  } catch {
    /* storage unavailable (private mode) — loop protection degrades, entry doesn't */
  }
}

/** Clear the cooldown so an explicit user retry isn't silently ignored. */
export function clearBridgeRedirect(): void {
  try {
    sessionStorage.removeItem(BRIDGE_REDIRECT_KEY);
  } catch {
    /* nothing to clear */
  }
}

/**
 * Everything that can leave a Microsoft-authenticated user without a Supabase
 * session — the exchange's own verdicts, plus MSAL failing to hand us a token
 * to exchange at all.
 */
export type BridgeFailureReason = ExchangeFailureReason | 'entra-silent-failed';

export interface BridgeNotice {
  title: string;
  body: string;
  /** The machine reason, shown as small print so a support conversation can
   *  start from a fact instead of "it says it's still loading". */
  detail?: string;
  /** Which recovery the card should lead with. */
  primary: 'reload' | 'signin';
}

/**
 * What the stalled-access card should say. Pure so the wording for each real
 * failure is pinned by tests rather than discovered in production.
 *
 * `null` means nothing has failed yet — the bridge or the access check is
 * merely slow, and a reload is the right first move. Every other case is a
 * definite answer from gotrue, and telling those users to check their
 * connection is what made this bug invisible for weeks.
 */
export function resolveBridgeNotice(reason: BridgeFailureReason | null): BridgeNotice {
  switch (reason) {
    case 'signup-disabled':
      return {
        title: 'Your Flow account isn’t set up yet',
        body:
          'Microsoft signed you in, but there’s no Flow account for this address yet. ' +
          'An administrator needs to add you before you can get in.',
        detail: 'Sign-in blocked: signup-disabled',
        primary: 'signin',
      };
    case 'user-banned':
      // A deactivated person (migration 274 bans their login). Not a glitch and
      // not "banned" in the sense the word suggests: an administrator removed
      // the access. Signing in again cannot help, so don't promise that it can.
      return {
        title: 'Your access to Flow has been removed',
        body:
          'Microsoft signed you in, but this Flow account has been deactivated. ' +
          'If you think that’s a mistake, ask an administrator to restore your access.',
        detail: 'Sign-in blocked: user-banned',
        primary: 'signin',
      };
    case 'missing-nonce':
    case 'nonce-mismatch':
    case 'entra-silent-failed':
      return {
        title: 'Finish signing in',
        body:
          'Your Microsoft sign-in needs to be refreshed before Flow can verify it. ' +
          'Signing in again takes a few seconds.',
        detail: `Sign-in blocked: ${reason}`,
        primary: 'signin',
      };
    case 'exchange-failed':
      return {
        title: 'Couldn’t complete sign-in',
        body:
          'Microsoft signed you in, but Flow couldn’t confirm it. ' +
          'Signing in again usually clears this.',
        detail: 'Sign-in blocked: exchange-failed',
        primary: 'signin',
      };
    default:
      return {
        title: 'Still verifying access…',
        body:
          'This is taking longer than usual. Your connection may have stalled — ' +
          'reloading the page usually fixes it.',
        primary: 'reload',
      };
  }
}
